import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import './ScHeader.css';

export default function ScHeader({
  user,
  onLoginClick,
  onUserUpdate,
  onLogout,
  logoLabel = 'Somnia Sleep Care',
  dashboardPath = '/dashboard',
}) {
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editAvatarPreview, setEditAvatarPreview] = useState(null);
  const [nameError, setNameError] = useState('');
  const avatarInputRef = useRef(null);

  const [profileRow, setProfileRow] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user) {
        setProfileRow(null);
        return;
      }
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();
      if (!authUser) {
        setProfileRow(null);
        return;
      }
      const { data: profile } = await supabase
        .from('profiles')
        .select('nickname, avatar_url')
        .eq('id', authUser.id)
        .maybeSingle();
      if (!cancelled) {
        setProfileRow(profile ?? null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const dropdownDisplayName =
    (profileRow?.nickname && String(profileRow.nickname).trim()) ||
    user?.user_metadata?.full_name ||
    user?.email ||
    '';
  const headerAvatarUrl = profileRow?.avatar_url || user?.user_metadata?.avatar_url || null;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!user) setEditModalOpen(false);
  }, [user]);

  const openEditModal = () => {
    setNameError('');
    setProfileOpen(false);

    const fallbackName = user?.user_metadata?.full_name || '';
    const fallbackAvatar = user?.user_metadata?.avatar_url || null;

    setEditName(profileRow != null ? (profileRow.nickname ?? fallbackName) : fallbackName);
    setEditAvatarPreview(
      profileRow != null ? (profileRow.avatar_url ?? fallbackAvatar) : fallbackAvatar
    );
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setNameError('');
    setEditModalOpen(false);
  };

  const handleLogoutClick = async () => {
    setProfileOpen(false);
    try {
      if (onLogout) await Promise.resolve(onLogout());
    } catch (e) {
      console.error(e);
    }
    navigate('/', { replace: true });
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (editAvatarPreview && String(editAvatarPreview).startsWith('blob:')) {
      URL.revokeObjectURL(editAvatarPreview);
    }
    setEditAvatarPreview(URL.createObjectURL(file));
  };

  const handleEditSave = async () => {
    if (!user || !onUserUpdate) return;
    const trimmed = editName.trim();
    if (!trimmed) {
      setNameError('이름을 입력해 주세요.');
      return;
    }
    setNameError('');

    const {
      data: { user: authUser },
      error: authErr,
    } = await supabase.auth.getUser();
    if (authErr || !authUser) {
      alert('로그인 정보를 확인할 수 없습니다. 다시 로그인해 주세요.');
      return;
    }

    const updatePayload = { nickname: trimmed };
    if (editAvatarPreview && !String(editAvatarPreview).startsWith('blob:')) {
      updatePayload.avatar_url = editAvatarPreview;
    }

    const { data: updatedRows, error: updateErr } = await supabase
      .from('profiles')
      .update(updatePayload)
      .eq('id', authUser.id)
      .select('id');

    if (updateErr) {
      console.error(updateErr);
      alert('프로필 저장에 실패했습니다. 잠시 후 다시 시도해 주세요.');
      return;
    }

    if (!updatedRows?.length) {
      const { error: insertErr } = await supabase.from('profiles').insert({
        id: authUser.id,
        nickname: trimmed,
        avatar_url: updatePayload.avatar_url ?? null,
      });
      if (insertErr) {
        console.error(insertErr);
        alert('프로필 저장에 실패했습니다. 잠시 후 다시 시도해 주세요.');
        return;
      }
    }

    const updated = {
      ...user,
      user_metadata: {
        ...user.user_metadata,
        full_name: trimmed,
        avatar_url: editAvatarPreview,
      },
    };
    onUserUpdate(updated);
    setEditModalOpen(false);
  };

  return (
    <>
      <header className="sc-header">
        <div className="sc-header-inner">
          <button
            type="button"
            className="sc-logo"
            onClick={() => navigate('/')}
            aria-label="홈으로 이동"
          >
            <span className="sc-logo-text">{logoLabel}</span>
          </button>
          {user ? (
            <div className="sc-profile-wrap" ref={profileRef}>
              <button
                type="button"
                className="sc-profile-btn"
                onClick={() => setProfileOpen((v) => !v)}
              >
                {headerAvatarUrl ? (
                  <img src={headerAvatarUrl} alt="profile" className="sc-profile-img" />
                ) : (
                  <span className="sc-profile-initial">
                    {(dropdownDisplayName || user.email || '?')[0].toUpperCase()}
                  </span>
                )}
              </button>
              {profileOpen && (
                <div className="sc-profile-dropdown">
                  <div className="sc-profile-dropdown-info">
                    <span className="sc-profile-dropdown-name">{dropdownDisplayName}</span>
                    <span className="sc-profile-dropdown-email">{user.email}</span>
                  </div>
                  <div className="sc-profile-dropdown-divider" />
                  <button type="button" className="sc-profile-menu-item" onClick={openEditModal}>
                    프로필 편집
                  </button>
                  <button
                    type="button"
                    className="sc-profile-menu-item"
                    onClick={() => navigate('/payments')}
                  >
                    결제 내역
                  </button>
                  <button
                    type="button"
                    className="sc-profile-menu-item"
                    onClick={() => {
                      setProfileOpen(false);
                      navigate(dashboardPath);
                    }}
                  >
                    대시보드로 가기
                  </button>
                  <button
                    type="button"
                    className="sc-profile-menu-item sc-profile-menu-logout"
                    onClick={handleLogoutClick}
                  >
                    로그아웃
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button type="button" className="sc-cta-btn" onClick={onLoginClick}>
              로그인
            </button>
          )}
        </div>
      </header>

      {editModalOpen && user && (
        <div className="sc-modal-overlay" onClick={closeEditModal}>
          <div className="sc-modal sc-login-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="sc-modal-close" onClick={closeEditModal}>
              ✕
            </button>
            <div className="sc-modal-header">
              <span className="sc-modal-badge">프로필 편집</span>
              <h3>프로필을 수정하세요</h3>
            </div>

            <div className="sc-edit-avatar-wrap">
              <div className="sc-edit-avatar">
                {editAvatarPreview ? (
                  <img src={editAvatarPreview} alt="avatar" className="sc-edit-avatar-img" />
                ) : (
                  <span className="sc-edit-avatar-initial">
                    {(editName || user?.email || '?')[0].toUpperCase()}
                  </span>
                )}
                {/* <div className="sc-edit-avatar-overlay">사진 변경</div> */}
              </div>
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleAvatarChange}
              />
            </div>

            <div className="sc-modal-form">
              <div className="sc-modal-name-field">
                <input
                  type="text"
                  placeholder="이름"
                  value={editName}
                  onChange={(e) => {
                    setEditName(e.target.value);
                    if (nameError) setNameError('');
                  }}
                  aria-invalid={!!nameError}
                  aria-describedby={nameError ? 'sc-profile-name-error' : undefined}
                />
                {nameError ? (
                  <p id="sc-profile-name-error" className="sc-modal-field-error" role="alert">
                    {nameError}
                  </p>
                ) : null}
              </div>
              <button type="button" className="sc-cta-btn sc-modal-submit" onClick={handleEditSave}>
                저장
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
